// Plays a loud, repeated two-tone chime for order alerts using the Web Audio
// API — no audio file to ship/host, and it works completely offline.
//
// Browsers block audio (and Notification-triggered sound) from firing before
// the user has interacted with the page at least once, so we lazily create —
// and "unlock" — a single shared AudioContext on the user's first gesture
// anywhere in the app (click, tap, or keypress). After that,
// playNotificationSound() just works for the rest of the session.

let audioContext: AudioContext | null = null;

// ── On/off switch (Settings → Notification Preferences) ────────────────────
// Saved on this device only, and on unless someone turns it off. While off,
// alerts keep running silently, so switching back on resumes any alarm that
// should still be ringing.
const SOUND_ENABLED_KEY = "fleetrify_alert_sound_enabled";
const soundListeners = new Set<(enabled: boolean) => void>();

let soundEnabled = (() => {
  try {
    return localStorage.getItem(SOUND_ENABLED_KEY) !== "false";
  } catch {
    return true;
  }
})();

export const isSoundEnabled = () => soundEnabled;

export const setSoundEnabled = (enabled: boolean) => {
  soundEnabled = enabled;
  try {
    localStorage.setItem(SOUND_ENABLED_KEY, String(enabled));
  } catch {
    // Storage blocked: the choice still applies until the page is reloaded.
  }
  soundListeners.forEach((listener) => listener(enabled));
};

// Every open Fleetrify tab runs its own alarm, so follow a change made in
// another tab straight away instead of ringing on until a refresh.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== SOUND_ENABLED_KEY) return;
    soundEnabled = event.newValue !== "false";
    soundListeners.forEach((listener) => listener(soundEnabled));
  });
}

export const subscribeSoundEnabled = (listener: (enabled: boolean) => void) => {
  soundListeners.add(listener);
  return () => {
    soundListeners.delete(listener);
  };
};

const getAudioContext = (): AudioContext | null => {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || (window as any).webkitAudioContext;
  if (!Ctx) return null;
  if (!audioContext) {
    audioContext = new Ctx();
    // Some browsers allow sound straight away (no click needed), so report
    // every state change, not just the ones our own resume() calls cause.
    audioContext.onstatechange = () => notifyAudioState();
    setTimeout(notifyAudioState, 0);
  }
  return audioContext;
};

if (typeof window !== "undefined") {
  const unlockOnce = () => {
    const ctx = getAudioContext();
    if (ctx?.state === "suspended") {
      ctx.resume().then(notifyAudioState).catch(() => {});
    }
  };
  // pointerdown/touchstart catch taps as well as clicks, and we deliberately
  // keep listening on every gesture (not just the first) — resume() is a
  // cheap no-op once the context is already running, and this way we get a
  // fresh chance to unlock even if the very first attempt didn't stick.
  window.addEventListener("pointerdown", unlockOnce);
  window.addEventListener("keydown", unlockOnce);
}

const audioStateListeners = new Set<(unlocked: boolean) => void>();

const notifyAudioState = () => {
  const unlocked = audioContext?.state === "running";
  audioStateListeners.forEach((listener) => listener(unlocked));
};

export const isAudioUnlocked = () => audioContext?.state === "running";

// Lets the UI know when the browser starts (or stops) allowing sound.
export const subscribeAudioState = (listener: (unlocked: boolean) => void) => {
  audioStateListeners.add(listener);
  return () => {
    audioStateListeners.delete(listener);
  };
};

// Must be called from a click/tap/keypress handler to count as a user gesture.
export const unlockAudio = async () => {
  const ctx = getAudioContext();
  if (!ctx) return false;
  if (ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch {
      return false;
    }
  }
  notifyAudioState();
  return ctx.state === "running";
};

// Shared output for the alarm: a hard limiter so the tones can be driven as
// hot as possible without crackling, then full-scale gain to the speakers.
let alarmOutput: AudioNode | null = null;

const getAlarmOutput = (ctx: AudioContext) => {
  if (!alarmOutput) {
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.001;
    limiter.release.value = 0.05;
    const makeup = ctx.createGain();
    makeup.gain.value = 1.8;
    limiter.connect(makeup);
    makeup.connect(ctx.destination);
    alarmOutput = limiter;
  }
  return alarmOutput;
};

const playTone = (
  ctx: AudioContext,
  frequency: number,
  startTime: number,
  duration: number,
  peakGain = 0.6,
  type: OscillatorType = "sine",
  destination: AudioNode = ctx.destination
) => {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();

  oscillator.type = type;
  oscillator.frequency.value = frequency;

  // Quick fade in/out so the tone doesn't click at the edges.
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(peakGain, startTime + 0.02);
  gain.gain.linearRampToValueAtTime(0, startTime + duration);

  oscillator.connect(gain);
  gain.connect(destination);

  oscillator.start(startTime);
  oscillator.stop(startTime + duration);
};

const scheduleChime = (ctx: AudioContext) => {
  const now = ctx.currentTime;
  const chimeSpacing = 0.42;

  for (let i = 0; i < 3; i++) {
    const start = now + i * chimeSpacing;
    playTone(ctx, 880, start, 0.16);
    playTone(ctx, 1175, start + 0.17, 0.22);
  }
};

/**
 * Plays a loud, attention-grabbing "new order" alert — a rising two-tone
 * chime repeated three times, so it's hard to miss over background noise
 * (unlike a single quiet ding).
 *
 * If the browser hasn't granted audio yet (no user gesture on the page this
 * session), this resumes the AudioContext first and only schedules the
 * chime once that succeeds — scheduling against a suspended context's stale
 * `currentTime` is why a naive version of this can silently play nothing.
 */
export const playNotificationSound = async () => {
  if (!soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  if (ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch {
      // Still blocked — most likely no user gesture has happened yet on
      // this tab this session. Nothing more we can do without one; the
      // toast/browser notification still carries the alert visually.
      console.warn(
        "[notificationSound] AudioContext is still suspended — the browser is blocking audio until the user interacts with the page (click/tap/keypress)."
      );
      return;
    }
  }

  if (ctx.state !== "running") return;

  scheduleChime(ctx);
};

// ── Looping order alarm ─────────────────────────────────────────────────────
// Rings until stopOrderAlarm() is called. Square waves through the limiter
// are much louder to the ear than the sine chime above. The page can't raise
// the computer's own volume, so this is as loud as the browser allows.

const ALARM_REPEAT_MS = 2200;
let alarmTimer: ReturnType<typeof setInterval> | null = null;
// While audio is blocked, resume() stays pending until the user clicks. Only
// keep one in flight, or every queued round would fire together on that click.
let resumePending = false;

const scheduleAlarm = (ctx: AudioContext) => {
  const out = getAlarmOutput(ctx);
  const now = ctx.currentTime;
  for (let i = 0; i < 4; i++) {
    const start = now + i * 0.3;
    playTone(ctx, 988, start, 0.13, 1, "square", out);
    playTone(ctx, 1319, start + 0.14, 0.14, 1, "square", out);
  }
};

// "Money in" chime for payments: a bright rising arpeggio, clearly different
// from the rapid square-wave beeping above.
const schedulePaidChime = (ctx: AudioContext) => {
  const out = getAlarmOutput(ctx);
  const now = ctx.currentTime;
  [1047, 1319, 1568, 2093].forEach((freq, i) => {
    playTone(ctx, freq, now + i * 0.12, 0.35, 1, "triangle", out);
    playTone(ctx, freq * 2, now + i * 0.12, 0.2, 0.35, "sine", out);
  });
  playTone(ctx, 2093, now + 0.6, 0.7, 1, "triangle", out);
  playTone(ctx, 2637, now + 0.6, 0.7, 0.6, "triangle", out);
};

type AlarmSound = "alarm" | "paid";
let roundIndex = 0;

// When different alerts are active together, take turns so each stays recognisable.
const playRound = (ctx: AudioContext) => {
  if (!soundEnabled) return;
  const sounds = Array.from(new Set(Array.from(activeReasons, (r) => REASON_SOUND[r])));
  if (sounds.length === 0) return;
  const sound = sounds[roundIndex++ % sounds.length];
  if (sound === "paid") schedulePaidChime(ctx);
  else scheduleAlarm(ctx);
};

const ringOnce = () => {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === "running") {
    playRound(ctx);
    return;
  }
  // Still blocked: start ringing the moment the browser allows it (after
  // any click, tap or keypress on the page).
  if (resumePending) return;
  resumePending = true;
  ctx.resume()
    .then(() => {
      notifyAudioState();
      if (ctx.state === "running" && alarmTimer) playRound(ctx);
    })
    .catch(() => {})
    .finally(() => {
      resumePending = false;
    });
};

// Several things can want the alarm at once (orders waiting on the company,
// a customer accepting an offer). It rings while any of them still does.
export type AlarmReason = "awaiting-company" | "customer-accepted" | "customer-paid";
const activeReasons = new Set<AlarmReason>();
const REASON_SOUND: Record<AlarmReason, AlarmSound> = {
  "awaiting-company": "alarm",
  "customer-accepted": "alarm",
  "customer-paid": "paid",
};
const alarmListeners = new Set<(reasons: ReadonlySet<AlarmReason>) => void>();

const syncAlarm = () => {
  if (activeReasons.size > 0 && !alarmTimer) {
    ringOnce();
    alarmTimer = setInterval(ringOnce, ALARM_REPEAT_MS);
  } else if (activeReasons.size === 0 && alarmTimer) {
    clearInterval(alarmTimer);
    alarmTimer = null;
  }
  alarmListeners.forEach((listener) => listener(new Set(activeReasons)));
};

export const subscribeOrderAlarm = (listener: (reasons: ReadonlySet<AlarmReason>) => void) => {
  alarmListeners.add(listener);
  return () => {
    alarmListeners.delete(listener);
  };
};

export const startOrderAlarm = (reason: AlarmReason) => {
  if (activeReasons.has(reason)) return;
  activeReasons.add(reason);
  syncAlarm();
};

export const stopOrderAlarm = (reason?: AlarmReason) => {
  if (reason) activeReasons.delete(reason);
  else {
    activeReasons.clear();
    timedStops.forEach(clearTimeout);
    timedStops.clear();
  }
  syncAlarm();
};

// Rings for a fixed time, then stops by itself. Calling it again for the same
// reason while it's still going restarts that countdown.
const timedStops = new Map<AlarmReason, ReturnType<typeof setTimeout>>();

export const ringOrderAlarmFor = (reason: AlarmReason, ms: number) => {
  const existing = timedStops.get(reason);
  if (existing) clearTimeout(existing);
  startOrderAlarm(reason);
  timedStops.set(
    reason,
    setTimeout(() => {
      timedStops.delete(reason);
      stopOrderAlarm(reason);
    }, ms)
  );
};

// "Play test sound" in settings. Runs from a click, so it also unlocks audio.
export const playTestAlarm = async () => {
  if (!(await unlockAudio()) || !audioContext) return false;
  scheduleAlarm(audioContext);
  return true;
};
